
import React, { useRef, useEffect, useState } from 'react'

import { EditorState, EditorSelection } from "@codemirror/state";
import { EditorView, dropCursor, hoverTooltip } from "@codemirror/view";

import { useDrop } from 'react-dnd';

import lang_parser_lezer from 'bwax/ml/lang/lang_parser_lezer.bs';

import './CodeMirrorEditor.less';

import './DefsCodeEditor.less';

import lang_entry from 'bwax/ml/lang/lang_entry.bs'
import lang_entry_base from 'bwax/ml/lang/lang_entry_base.bs'

import { nextDiagnostic } from "@codemirror/lint"

import fragment_helper from 'bwax/ml/utils/fragment_helper.bs';
import { packV4, tag } from 'bwax/lang/LangHelper'

import {
    isInvolvingDynamicData,
    isInvolvingTargetRecord,
} from 'bwax/BwaxExecHelper';

import setupCodeEditor from './setupCodeEditor';

import CodeOutline from './CodeOutline';
import uniq from 'lodash/uniq';


import prettyPrintLang from 'bwax/ml/printer/prettyPrintLang';

export default function DefsCodeEditor(props) {

    const {
        sourceCode,
        targetEntityName,
        typingEnv, // [ tenv, dts, entity_dict, data_type_dict ]

        setResultedTypingEnv,

        onChange,
        markError,

        bindCommands,  // 
        // 
        renderDropDialog,

        onTypedTreeChange,

    } = props;

    // 在 linter 里面访问：
    const typingEnvRef = useRef(typingEnv);
    typingEnvRef.current = typingEnv;

    const editorRef = useRef();
    const viewRef = useRef();

    const dropAtRef = useRef()

    const [typedTree, setTypedTree] = useState();
    // 用于 drop handler
    const typedTreeRef = useRef();
    typedTreeRef.current = typedTree;

    // { tree, codeText }
    const [syntaxTree, setSyntaxTree] = useState();

    const [currentDropped, setCurrentDropped] = useState();

    // given back
    if (bindCommands) {
        bindCommands({

            focus: () => {
                viewRef.current.focus();
            },

            jumpToError: () => nextDiagnostic(viewRef.current),
            // get specical code range:
            getSyntaxTree: () => {
                return syntaxTree
            },

            updateCodeText: codeText => {
                if (viewRef.current && viewRef.current.state.doc) {
                    // 
                    const currentDoc = viewRef.current.state.doc;
                    const docText = currentDoc.toJSON().join("\n");
                    if (docText != codeText) {
                        viewRef.current.dispatch({
                            changes: {
                                from: 0,
                                to: currentDoc.length,
                                insert: codeText,
                            }
                        })
                    }

                }
            },
            // [ from, to, codeText ]
            updateCodeRanges: updates => {
                if (viewRef.current) {
                    viewRef.current.dispatch(...updates.map(u => ({
                        changes: {
                            from: u.from,
                            to: u.to || u.from,
                            insert: u.codeText,
                        }
                    }))) 
                }
            }
        })
    }

    useEffect(() => {

        const [dropCursorPos, drawDropCursor] = dropCursor();

        const afterLinter = (codeText, tree) => {

            const typingEnv = typingEnvRef.current;

            const t0 = performance.now();
            // parse and type:
            const [result, parseError] = lang_parser_lezer.convert_defs_entry(codeText, tree);
            const t00 = performance.now();
            console.log("Conversion uses", t00 - t0);

            function handleError([message, startIndex, endIndex]) {
                const [from, to] = (() => {
                    if (startIndex == endIndex) {
                        if (startIndex > 0) {
                            return [startIndex - 1, endIndex]
                        } else if (endIndex < codeText.length) {
                            return [startIndex, endIndex + 1]
                        }
                    }
                    return [startIndex, endIndex]
                })();
                const errors = [{ message, from, to, severity: "error" }];
                return errors
            }


            if (parseError) {
                return handleError(parseError);

            } else if (typingEnv) {
                const [base_tenv, base_dts, entity_dict, data_type_dict] = typingEnv

                const [ast, _] = result;

                const t1 = performance.now();

                const [typed_ast, slim_ast, tyenv, dts, external_names_with_types, error] =
                    lang_entry.type_defs_to_js(base_dts, base_tenv, ast);

                const t1_1 = performance.now();

                console.log("Typing uses", t1_1 - t1);

                if (error) {
                    return handleError(error);
                } else {

                    const external_names = (external_names_with_types || []).map(e => e[0]);

                    // 从 Query_xxx 或者 Cmd_xxx 获得
                    const entity_names_from_external_names = external_names.map(name => {
                        const matched = /^(Query|Cmd)_([^\.]+)/.exec(name);
                        if (matched) {
                            const matchedName = matched[2];
                            return fragment_helper.entity_name_for_normalized_name(entity_dict, matchedName);
                        } else {
                            return null
                        }
                    }).filter(x => !!x);


                    // 另外还要检测一下所有的 dts 找到 E_Xxxx
                    // 这个也不一定全面
                    // const entity_names_from_dts = lang_entry_base.list_to_array(lang_entry.get_entity_names_from_dts(entity_dict, dts));

                    // 这个应该就比较全了：
                    const entity_names_from_ast = lang_entry_base.list_to_array(lang_entry.get_entity_names_from_typed_ast(entity_dict, typed_ast));

                    const used_entity_names = uniq([
                        // ...entity_names_from_dts, 
                        ...entity_names_from_external_names,
                        ...entity_names_from_ast
                    ])

                    const [name, defs] = slim_ast;
                    const compiled = packV4({
                        name, defs, dts, entityNames: used_entity_names, externalNames: external_names,
                    })

                    console.log("Compiled string length", compiled.length);

                    function makePreviewData() {

                        const usedSigs = fragment_helper.get_fragment_function_sigs_as_js(dts, external_names_with_types);
                        const involvingDynamicData = isInvolvingDynamicData(slim_ast, {
                            targetEntityName,
                            entity_dict, data_type_dict, dts
                        })

                        const ioTypeMetas = (() => {
                            const funcs = fragment_helper.get_func_types_as_js(defs);

                            const initType = funcs["init"];
                            const viewType = funcs["view"];

                            if (initType && viewType) {
                                // handle the meta data types
                                const initParamTypes = fragment_helper.get_interface_param_types_as_js(initType);

                                const initParamTypeMetas = initParamTypes.map(t => {
                                    const typeMeta = fragment_helper.to_type_metadata(entity_dict, data_type_dict, dts, t);
                                    return typeMeta
                                })

                                const viewParamTypes = fragment_helper.get_view_param_types_as_js(viewType);
                                const viewParamTypeMetas = viewParamTypes.map(t => {
                                    return fragment_helper.to_type_metadata(entity_dict, data_type_dict, dts, t);
                                })

                                const viewReturnTypes = fragment_helper.get_interface_return_types_as_js(viewType);
                                const viewReturnTypeMetas = viewReturnTypes.map(t => {
                                    return fragment_helper.to_type_metadata(entity_dict, data_type_dict, dts, t);
                                })

                                const isTargetingRecord = targetEntityName ?
                                    isInvolvingTargetRecord(slim_ast, {
                                        entity_dict, data_type_dict, dts, targetEntityName
                                    }) : false;

                                return {
                                    initParamTypes: tag(initParamTypes),
                                    initParamTypeMetas,

                                    ...(viewParamTypes && viewParamTypes.length > 0 ? {
                                        viewParamTypes: tag(viewParamTypes),
                                        viewParamTypeMetas,
                                    } : {}),

                                    viewReturnTypes: tag(viewReturnTypes),
                                    viewReturnTypeMetas,
                                    isTargetingRecord
                                };
                            }
                            // undefined
                        })();


                        return {
                            usedSigs, // 目前只用于检测 Page 对 PageFragment 的兼容性

                            involvingDynamicData, // 这个只是目前用于虚拟字段

                            externalNames: external_names,  // 记录依赖的外部函数，用于或者 depended fragments

                            ioTypeMetas, // 用于虚拟字段或者数据接口

                            // 用于 design 端的整合 fragments 的 tyenv； 以及在其他地方快速检测该代码声明的各个值（和函数）的类型
                            tyenv: JSON.stringify(tag(
                                lang_entry.only_exposed_tyenv(tyenv)
                                // tyenv
                            ))

                            // testInputParams 是在 CompositeEditor 里保存的
                        }

                    }

                    const previewData = makePreviewData();

                    onChange({
                        src: codeText,
                        compiled,
                        previewData,
                    });

                    if (setResultedTypingEnv) {
                        setResultedTypingEnv([
                            lang_entry_base.merge_env(base_tenv, tyenv),
                            lang_entry_base.merge_dts(base_dts, dts),
                            entity_dict,
                            data_type_dict,
                        ])
                    }

                    setTypedTree(typed_ast);
                    onTypedTreeChange(typed_ast);

                    setTimeout(() => {
                        setSyntaxTree({ tree, codeText });
                    }, 1)


                    return []
                }
            }
            return []

        };

        const state = EditorState.create({
            // doc: value || storedItem || "",
            // doc: storedItem || "",
            doc: sourceCode || "",
            extensions: [
                ...setupCodeEditor({
                    afterLinter: (codeText, tree) => {
                        const errors = afterLinter(codeText, tree);
                        // send back to the upper layer:
                        markError(errors && errors.length > 0 ? errors : undefined)
                        return errors;

                    }
                }),

                [dropCursorPos, drawDropCursor],

                hoverTooltip((view, pos, side) => {
                    // console.log(">>> HOVER", view, pos, side)

                    // 1. 看看当前 hover 的是否一个 name
                    // 2. 确定当前 name 的类型
                    // 3. 显示类型（通过 React 构建, 直接 render 到一个新的 div 中，再返回）

                }),

                EditorView.updateListener.of(update => {
                    if (update.state.field(dropCursorPos) !== null) {
                        dropAtRef.current = view.state.field(dropCursorPos);
                    }

                }),
            ]
        });

        const view = new EditorView({ state, parent: editorRef.current });
        viewRef.current = view;

        return () => {
            view.destroy();
            // editorRef.current.removeEventListener("input", log);
        };
    }, []);


    const [collectedProps, drop] = useDrop(() => ({
        accept: [
            "entity",
            "field",
            "virtual-field",
            "backlink-field",
            // "data-interface-query",
            // "data-interface-operation",
            "data-interface",
            "admin-page",
            "page",
            "page-component",
            "page-fragment",
            "event-handler",
            "scheduled-event-handler",

            // 
            "imported-entity"
        ],

        drop: (item) => {
            const dropAt = dropAtRef.current;

            setCurrentDropped({ item, dropAt });

        },
    }))

    drop(editorRef);


    function formatDocument(view, prettyPrint) {
        const state = view.state;

        const currentSelection = state.selection;
        if (currentSelection && currentSelection.ranges.length > 0) {
            const { from, to } = currentSelection.ranges[0];
            if (from < to) {
                // only format the selection
                const part = state.doc.sliceString(from, to);
                // console.log(">> format the part", part);

                const formatted = prettyPrintLang(80, part);
            }
        }

        // format the entire document
        // console.log(">>> format the entire document")
    }


    return (
        <div className="defs-code-editor">
            <div className="code-mirror-editor" ref={editorRef}
                onKeyDown={e => {
                    if (e.shiftKey && e.altKey && e.code == "KeyF") {
                        e.preventDefault();

                        const view = viewRef.current;
                        const state = view.state;

                        formatDocument(view, prettyPrintLang);

                        // view.dispatch({
                        //     changes: {
                        //         from: 0,
                        //         to: state.doc.length,
                        //         insert: "---- Demo"   
                        //     }
                        // })
                        // viewRef.current.dispatch({
                        //     newDoc: viewRef.current.state.toText("---- Demo")
                        // })

                    }
                }}
            />
            {syntaxTree ? <CodeOutline tree={syntaxTree.tree} codeText={syntaxTree.codeText} onClickAt={node => {

                // const docLine = editorState.doc.lineAt(at);
                if (node) {
                    // 代码参照 @codemirror/search
                    // 文档见 https://codemirror.net/docs/ref/#state.Transaction
                    // https://codemirror.net/docs/ref/#view.EditorView^scrollIntoView

                    const nextSelection = EditorSelection.cursor(node.from);

                    // 在 select 代码块的首行
                    viewRef.current.dispatch({
                        // selection: EditorSelection.cursor(node.from),
                        // scrollIntoView: true
                        selection: nextSelection,
                        effects: [
                            EditorView.scrollIntoView(nextSelection, { y: "center" })
                        ]
                    });
                }

            }} /> : null}
            {renderDropDialog ? renderDropDialog({ currentDropped, setCurrentDropped, view: viewRef.current }) : null}
        </div>
    );

}

